/**
 * Result codes returned by Accept.js responses.
 */
export enum AcceptResultCode {
  /** Indicates a successful request. */
  Ok = 'Ok',

  /** Indicates a failed request with one or more error messages. */
  Error = 'Error',
}

/**
 * Represents an error or informational message returned by Accept.js.
 */
export interface AcceptMessage {
  /** Short code identifying the message (e.g., "E_WC_05"). */
  code: string;
  /** Human-readable description of the message. */
  text: string;
}

/**
 * Tokenized payment information returned by Accept.js.
 *
 * This data is safe to send to your server in place of raw payment details.
 */
export interface AcceptOpaqueData {
  /** Descriptor that identifies the type of opaque data (e.g., "COMMON.ACCEPT.INAPP.PAYMENT"). */
  dataDescriptor: string;
  /** The actual tokenized value representing payment details. */
  dataValue: string;
}

/**
 * Response payload returned by Accept.js when a request succeeds.
 */
export interface AcceptOkResponse {
  /** Opaque tokenized payment data. */
  opaqueData: AcceptOpaqueData;

  /** Response messages indicating success. */
  messages: {
    resultCode: AcceptResultCode.Ok;
  };
}

/**
 * Response payload returned by Accept.js when a request fails.
 */
export interface AcceptErrorResponse {
  /** Response messages containing error codes and descriptions. */
  messages: {
    resultCode: AcceptResultCode.Error;
    message: AcceptMessage[];
  };
}

/**
 * Union type representing either a successful or failed response.
 */
export type AcceptResponse = AcceptOkResponse | AcceptErrorResponse;

/**
 * Bank account details used for eCheck.Net transactions.
 */
export interface AcceptBankData {
  /**
   * The bank account number. Numeric string. Up to 17 digits.
   */
  accountNumber: string;
  /**
   * The ABA routing number. Numeric string. 9 digits.
   */
  routingNumber: string;
  /**
   * The name of the person who holds the bank account. Up to 22 characters.
   */
  nameOnAccount: string;
  /**
   * The type of bank account used for the eCheck.Net transaction.
   */
  accountType: 'checking' | 'savings' | 'businessChecking';
}

/**
 * Credit/debit card details used for card transactions.
 */
export interface AcceptCardData {
  /**
   * Must be a valid 13-16 digit card number.
   */
  cardNumber: string;
  /**
   * 2-digit month.
   */
  month: string;
  /**
   * 2 or 4-digit year.
   */
  year: string;
  /**
   * 3 or 4-digit Card Code
   */
  cardCode?: string;
  /**
   * Alphanumeric postal code. 20-character maximum.
   */
  zip?: string;
  /**
   * Alphanumeric cardholder name. 64-character maximum.
   */
  fullName?: string;
}

/**
 * Request payload sent to Accept.js for tokenization.
 */
export type AcceptDispatchRequest = {
  /** Authentication details for your Authorize.Net account. */
  authData: {
    /** Public client key provided by Authorize.Net. */
    clientKey: string;

    /** API login ID provided by Authorize.Net. */
    apiLoginID: string;
  };

  /** Card data to tokenize (mutually exclusive with bankData). */
  cardData?: AcceptCardData;

  /** Bank data to tokenize (mutually exclusive with cardData). */
  bankData?: AcceptBankData;
};
